export interface StoreChangeContext {
  isCurrent(): boolean;
  tryCommit(): boolean;
}

export type StoreChangeKind = 'address' | 'time' | 'qr-order-type' | 'refresh' | 'menu-rebuild' | 'consent' | 'membership' | 'cart';

/** Coalesce consecutive pending selections; finish committed cart work in order. */
export class StoreChangeQueue {
  private readonly queues = new Map<string, {
    tail: Promise<void>;
    last?: { kind: StoreChangeKind; superseded: boolean };
  }>();

  run<T>(key: string, isActive: () => boolean, work: (change: StoreChangeContext) => Promise<T>,
    kind: StoreChangeKind): Promise<T | undefined> {
    let queue = this.queues.get(key);
    if (!queue) {
      queue = { tail: Promise.resolve() };
      this.queues.set(key, queue);
    }
    // A time change does not perform the address change's cart updates (or vice versa).
    // Only consecutive requests of the same kind can replace one another.
    if (kind !== 'cart' && kind !== 'membership' && kind !== 'consent' && queue.last?.kind === kind) queue.last.superseded = true;
    const selection = { kind, superseded: false };
    queue.last = selection;
    let committed = false;
    const change: StoreChangeContext = {
      isCurrent: () => isActive() && (committed || !selection.superseded),
      tryCommit: () => {
        if (!change.isCurrent()) return false;
        committed = true;
        return true;
      }
    };
    const result = queue.tail.then(() => change.isCurrent() ? work(change) : undefined);
    // A failed operation must not poison the queue or leave an unhandled tail.
    const tail = result.then(() => undefined, () => undefined);
    queue.tail = tail;
    void tail.then(() => {
      if (queue.tail === tail) this.queues.delete(key);
    });
    return result;
  }
}
